import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Users, 
  Question, 
  Exam, 
  CurrencyDollar,
  TrendUp,
  CheckCircle,
  XCircle,
  SpinnerGap,
  Warning
} from '@phosphor-icons/react';
import { cn } from '../../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminDashboardPage = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/analytics`, { withCredentials: true });
      setAnalytics(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <SpinnerGap className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Warning weight="fill" className="w-12 h-12 text-destructive mx-auto mb-4" />
          <p className="text-destructive">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="font-heading font-black text-3xl tracking-tighter">Dashboard</h1>
        <p className="text-muted-foreground">Overview of your platform metrics</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          icon={Users}
          label="Total Users"
          value={analytics.users.total}
          subtext={`+${analytics.users.new_last_7_days} this week`}
          color="text-blue-500"
        />
        <StatCard
          icon={CurrencyDollar}
          label="Premium Users"
          value={analytics.users.premium}
          subtext={`${analytics.users.conversion_rate}% conversion`}
          color="text-success"
        />
        <StatCard
          icon={Question}
          label="Questions"
          value={analytics.questions.total}
          subtext={`${Object.keys(analytics.questions.by_category).length} categories`}
          color="text-accent"
        />
        <StatCard
          icon={Exam}
          label="Mock Exams"
          value={analytics.exams.total}
          subtext={`${analytics.exams.pass_rate}% pass rate`}
          color="text-primary"
        />
      </div>

      {/* Revenue & Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Revenue Card */}
        <div className="bg-card border border-border rounded-sm p-6">
          <h2 className="font-heading font-bold text-lg mb-4">Revenue</h2>
          <div className="flex items-baseline gap-2 mb-4">
            <span className="font-heading font-black text-4xl text-success">
              ${analytics.revenue.estimated_monthly}
            </span>
            <span className="text-muted-foreground">/month (estimated)</span>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Premium price</span>
              <span>${analytics.revenue.premium_price}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Premium subscribers</span>
              <span>{analytics.users.premium}</span>
            </div>
          </div>
        </div>

        {/* Activity Card */}
        <div className="bg-card border border-border rounded-sm p-6">
          <h2 className="font-heading font-bold text-lg mb-4">Activity</h2>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-muted-foreground">Total answers submitted</span>
                <span className="font-bold">{analytics.activity.total_answers}</span>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-muted-foreground">Answers last 7 days</span>
                <span className="font-bold">{analytics.activity.answers_last_7_days}</span>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-muted-foreground">Exams last 30 days</span>
                <span className="font-bold">{analytics.exams.recent_30_days}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Exam Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-card border border-border rounded-sm p-6">
          <h2 className="font-heading font-bold text-lg mb-4">Exam Results</h2>
          <div className="flex items-center gap-8">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle weight="fill" className="w-5 h-5 text-success" />
                <span className="text-2xl font-bold">{analytics.exams.passed}</span>
                <span className="text-muted-foreground">passed</span>
              </div>
              <div className="flex items-center gap-2">
                <XCircle weight="fill" className="w-5 h-5 text-destructive" />
                <span className="text-2xl font-bold">{analytics.exams.failed}</span>
                <span className="text-muted-foreground">failed</span>
              </div>
            </div>
            <div className="w-24 h-24 rounded-full border-8 border-success flex items-center justify-center"
                 style={{ borderColor: `hsl(var(--success))`, borderRightColor: 'hsl(var(--muted))' }}>
              <span className="font-heading font-black text-xl">{analytics.exams.pass_rate}%</span>
            </div>
          </div>
        </div>

        {/* Questions by Category */}
        <div className="bg-card border border-border rounded-sm p-6">
          <h2 className="font-heading font-bold text-lg mb-4">Questions by Category</h2>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {Object.entries(analytics.questions.by_category).map(([cat, count]) => (
              <div key={cat} className="flex justify-between text-sm">
                <span className="text-muted-foreground capitalize">{cat}</span>
                <span className="font-mono">{count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Difficult Questions */}
      <div className="bg-card border border-border rounded-sm p-6">
        <h2 className="font-heading font-bold text-lg mb-4">Most Difficult Questions</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Questions with the lowest ease factors (users struggle most with these)
        </p>
        {analytics.difficult_questions.length > 0 ? (
          <div className="space-y-3">
            {analytics.difficult_questions.map((q, idx) => (
              <div key={q.question_id} className="flex items-start gap-3 p-3 bg-muted rounded-sm">
                <span className="w-6 h-6 rounded-sm bg-destructive/20 text-destructive flex items-center justify-center text-xs font-bold">
                  {idx + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">{q.question_text}</p>
                  <div className="flex gap-4 mt-1 text-xs text-muted-foreground">
                    <span>Category: {q.category}</span>
                    <span>Ease: {q.avg_ease_factor}</span>
                    <span>Attempts: {q.attempts}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">
            No data yet. Questions will appear here once users start practicing.
          </p>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, subtext, color }) => (
  <div className="bg-card border border-border rounded-sm p-6">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm text-muted-foreground mb-1">{label}</p>
        <p className="font-heading font-black text-3xl">{value}</p>
        <p className="text-xs text-muted-foreground mt-1">{subtext}</p>
      </div>
      <Icon weight="fill" className={cn("w-8 h-8", color)} />
    </div>
  </div>
);
